import React, { useRef } from 'react';
import styled from '@emotion/styled';
import withEvents from 'analytics/withEvents';
import { SpacingScale } from 'ds/theme/spacing';
import { useScreenBreakpoint } from 'consts/breakpoints';
import { IAd, IndexedAd } from './types';
import { useSingletonIntersectionFullEntryObserver } from 'hooks/useSingletonIntersectionObserver';
import { Link } from 'components/link';
import CloseIcon from 'assets/svg/close.svg';
import { colorToAlpha } from 'helpers/insight';


const DEFAULT_HEIGHT = {
  mobile: '50px',
  desktop: '96px',
};

interface RootStyleProps {
  height?: React.CSSProperties['height'];
  mTop?: SpacingScale;
  mBottom?: SpacingScale;
}

const Root = styled.div<RootStyleProps>`
  ${({ theme, height, mTop, mBottom }) => `
    height: ${height};
    margin: ${mTop ? theme.spacing(mTop) : 0} auto ${mBottom ? theme.spacing(mBottom) : 0};
  `}
  display: flex;
  flex-direction: row;
  justify-content: center;
`;

const BannerWrapper = styled.div<{ fullWidth: boolean }>`
  position: relative;
  ${({ fullWidth }) => fullWidth ? `width: 100%;` : ''}
`;

const CloseIconWrapper = styled.div`
  width: 16px;
  height: 16px;
  position: absolute;
  left: 0;
  top: 0;
  background: ${({ theme }) => colorToAlpha(theme.colors.neutrals.white, 0.8)};
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

const Img = styled.img`
  max-width:100%;
  max-height:100%;
  margin: auto;
  display: block;
`;

export type ProjectAdBannerProps = RootStyleProps & {
  onAdClick?: (ad: IAd) => void;
  sendBannerCloseEvent?: (ad: IAd) => void;
  ad: IndexedAd;
  eventSource?: string;
  closable?: boolean;
  onClose?: () => void;
  fullWidth?: boolean;
};

const ProjectAdBannerWrapper: React.FC<ProjectAdBannerProps> = (props) => {
  if (!props.ad) return null;

  return <ProjectAdBanner {...props} />;
};

const ProjectAdBanner: React.FC<ProjectAdBannerProps> = props => {
  const { height, mTop, mBottom, onAdClick, ad, eventSource, closable, onClose, sendBannerCloseEvent, fullWidth } = props;
  const { imageUrl, imageAlt, routeName, routeParams, link, isExternalLink, onBannerIntersection } = ad;

  const screenBreakpoint = useScreenBreakpoint();
  const rootRef = useRef<HTMLDivElement>(null);

  useSingletonIntersectionFullEntryObserver((entry) => {
    onBannerIntersection(entry, ad, ad.dynamicId);
  }, rootRef);


  const handleAnchorClick = () => {
    if (onAdClick) {
      onAdClick(ad);
    }
  };

  const handleClose = () => {
    onClose();
    sendBannerCloseEvent(ad);
  };

  const imageElement = <Img width={fullWidth ? '100%' : undefined} src={imageUrl} alt={imageAlt} />;

  return (
    <Root
      data-auto="project-ad-banner"
      height={fullWidth ? 'auto' : (height || (screenBreakpoint === 1 ? DEFAULT_HEIGHT.mobile : DEFAULT_HEIGHT.desktop))}
      mTop={mTop}
      ref={rootRef}
      mBottom={mBottom}
    >
      <BannerWrapper fullWidth={fullWidth}>
        {closable && (
          <CloseIconWrapper onClick={handleClose}>
            <CloseIcon width={8} height={8} />
          </CloseIconWrapper>
        )}
        {isExternalLink ? (
          <a href={link} target="_blank" rel="nofollow"  onClick={handleAnchorClick}>
            {imageElement}
          </a>
        ) : (
          <Link
            routeName={routeName}
            routeParams={{ ...routeParams, tracking_event_source: eventSource || undefined }}
            onClick={handleAnchorClick}
            hideLinkParams
          >
            {imageElement}
          </Link>
        )}
      </BannerWrapper>
    </Root>
  );
};

export default withEvents<ProjectAdBannerProps>(sendEvent => ({
  onAdClick(ad) {
    const { id, isExternalLink, link } = ad;
    sendEvent('banner_click', 'banner', {
      event: {
        banner_id: id,
        banner_url: link,
        banner_is_external: isExternalLink,
      },
    });
  },
  sendBannerCloseEvent({ id, isExternalLink, link }: IAd) {
    sendEvent('banner_close', 'banner', {
      event: {
        banner_id: id,
        banner_is_external: isExternalLink,
        banner_url: link,
      },
    });
  },
}))(ProjectAdBannerWrapper);
